import { ThemeEvents, CartUpdateEvent } from '@theme/events';

/**
 * Adds checked paid extras (liens plus longs / boutons) to the cart as
 * Shopify "nested cart lines" attached to the calot that was just added —
 * they render indented under it everywhere (cart, checkout, order,
 * confirmation emails) using the theme's built-in nested-line styling,
 * instead of showing up as an unrelated top-level cart line.
 *
 * Deliberately a *separate* /cart/add.js call rather than merging into the
 * main product form submission — keeps the core add-to-cart flow untouched
 * and avoids any risk of breaking it sitewide.
 */
class ProductAddonOptions extends HTMLElement {
  connectedCallback() {
    document.addEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate);
  }

  disconnectedCallback() {
    document.removeEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate);
  }

  #onCartUpdate = async (event) => {
    const { data } = event.detail || {};
    if (!data || data.didError) return;
    // Only react to the main product form's own add-to-cart, not our own
    // follow-up add (source below) or other components (quick-add, etc.)
    if (data.source !== 'product-form-component') return;
    if (data.productId?.toString() !== this.dataset.productId?.toString()) return;

    const checkedBoxes = Array.from(this.querySelectorAll('input[type="checkbox"]:checked'));
    if (checkedBoxes.length === 0) return;

    const parentLineKey = await this.#findParentLineKey();
    if (!parentLineKey) return;

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionIds = Array.from(cartItemsComponents)
      .map((el) => (el instanceof HTMLElement ? el.dataset.sectionId : null))
      .filter(Boolean);

    const items = checkedBoxes
      .map((checkbox) => ({
        id: Number(checkbox.dataset.variantId),
        quantity: 1,
        parent_line_key: parentLineKey,
      }))
      .filter((item) => item.id);

    if (items.length === 0) return;

    try {
      const response = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/javascript',
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: JSON.stringify({ items, sections: sectionIds.join(',') }),
      });

      const result = await response.json();
      if (result.status) {
        console.error('Product addon add-to-cart error:', result.message);
        return;
      }

      checkedBoxes.forEach((checkbox) => (checkbox.checked = false));

      document.dispatchEvent(
        new CartUpdateEvent(result, this.id || 'product-addon-options', {
          source: 'product-addon-options',
          sections: result.sections,
        })
      );
    } catch (error) {
      console.error('Product addon add-to-cart failed:', error);
    }
  };

  /**
   * Finds the cart line key of the calot that was just added, so the
   * add-ons can be attached to it as nested lines.
   * @returns {Promise<string | null>}
   */
  async #findParentLineKey() {
    const form = this.closest('.shopify-section, dialog, product-card')?.querySelector('product-form-component');
    const variantInput = /** @type {HTMLInputElement | null} */ (
      (form || document).querySelector('product-form-component input[name="id"]')
    );
    const variantId = variantInput?.value;
    if (!variantId) return null;

    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();
      // Most recently added top-level line for this variant (no parent of its own)
      const matches = cart.items.filter(
        (item) => item.variant_id?.toString() === variantId.toString() && !item.parent_relationship?.parent_key
      );
      return matches.length ? matches[matches.length - 1].key : null;
    } catch (error) {
      console.error('Product addon: could not resolve parent line key:', error);
      return null;
    }
  }
}

if (!customElements.get('product-addon-options')) {
  customElements.define('product-addon-options', ProductAddonOptions);
}
